<template>
  <div class="space-y-6 lg:space-y-6">
    <div>
      <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div class="flex-1">
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.TITLE') }}
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.DESCRIPTION') }}
          </p>
        </div>

        <div class="flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-2">
          <woot-button
            size="small"
            class="!bg-grass-500 !text-white w-full xl:w-auto order-1 xl:order-3"
            @click="$emit('add-sample')"
          >
            <lucide-icon name="plus" size="16" class="mr-2" />
            {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.ADD_NEW') }}
          </woot-button>

          <div class="flex gap-2 order-2 xl:order-1 flex-wrap">
            <woot-button
              class="!bg-grass-500 !text-white flex-1 xl:flex-none"
              size="small"
              :loading="isExporting"
              :disabled="isExporting"
              @click="onExport()"
            >
              <lucide-icon name="download" size="14" class="mr-1 lg:mr-2" />
              <span class="hidden xs:inline">{{ $t('GENERAL.DOWNLOAD') }}</span> XLSX
            </woot-button>
            <woot-button
              class="!bg-grass-500 !text-white flex-1 xl:flex-none"
              size="small"
              :loading="isImporting"
              :disabled="isImporting"
              @click="triggerImport"
            >
              <lucide-icon name="upload" size="14" class="mr-1 lg:mr-2" />
              <span class="hidden xs:inline">{{ $t('GENERAL.UPLOAD') }}</span> XLSX/CSV
            </woot-button>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            class="hidden"
            @change="onFileSelected"
          />
        </div>
      </div>
      <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.IMPORT_HINT') }}
      </p>
    </div>

    <div class="flex flex-col xl:flex-row gap-4">
      <div class="flex-1">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="`🔍 ${$t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.SEARCH_PLACEHOLDER')}`"
            class="w-full pl-4 pr-10 py-3 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md placeholder:text-slate-400"
            @input="triggerSearch"
          />
          <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div class="w-4 h-4 border-2 border-slate-200 dark:border-slate-600 border-t-hichat-500 rounded-full animate-spin"></div>
          </div>
        </div>
      </div>

      <!-- Sort by created date -->
      <div class="w-full xl:w-auto">
        <select
          v-model="selectedSort"
          class="w-full xl:w-44 pl-4 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
          style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
          @change="onSortChange"
        >
          <option value="created_at_desc">{{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.SORT.NEWEST') }}</option>
          <option value="created_at_asc">{{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.SORT.OLDEST') }}</option>
        </select>
      </div>
    </div>

    <BulkActionsBar
      :selected-count="selectedCount"
      :bulk-action="bulkAction"
      :is-bulk-deleting="isBulkDeleting"
      :is-all-selected-on-page="isAllSelectedOnPage"
      @activate="emitBulkStatus(true)"
      @deactivate="emitBulkStatus(false)"
      @delete="$emit('bulk-delete')"
      @clear-selection="$emit('clear-selection')"
      @toggle-select-page="toggleSelectOnPage"
    />

    <div>
      <div v-if="isLoading" class="py-8 flex items-center justify-center">
        <Spinner size="md" color-scheme="primary" />
      </div>

      <div v-else-if="!samples.length" class="text-center py-12">
        <div class="bg-white/60 dark:bg-slate-800/60 rounded-2xl p-8 border border-white/30 dark:border-slate-600/30 max-w-md mx-auto">
          <fluent-icon icon="book-contacts" size="48" class="text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.EMPTY_TITLE') }}
          </h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.EMPTY_DESCRIPTION') }}
          </p>
          <woot-button @click="$emit('add-sample')" variant="smooth" class="input-base">
            {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.ADD_FIRST') }}
          </woot-button>
        </div>
      </div>

      <div v-else class="space-y-4">
        <div
          v-for="sample in samples"
          :key="sample.id"
          class="group bg-white/60 dark:bg-slate-800/60 rounded-md p-2 border border-n-gray-1 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-800/80 hover:border-blue-200/50 dark:hover:border-blue-500/30 hover:shadow-lg transition-all duration-300"
        >
          <div class="flex items-start gap-2 sm:gap-3">
            <!-- Checkbox -->
            <input
              type="checkbox"
              class="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-slate-300 text-hichat-500 focus:ring-hichat-400"
              :checked="selectedIds.includes(sample.id)"
              @change="onToggleSelect(sample.id, $event.target.checked)"
            />

            <!-- Content -->
            <div class="flex-1 min-w-0">
              <!-- Title + Status -->
              <div class="flex flex-wrap items-center gap-2">
                <h3
                  class="text-base font-semibold text-slate-900 dark:text-slate-100 break-words"
                  v-html="hl(sample.title)"
                />
                <span
                  class="flex-shrink-0 px-2 py-0.5 rounded-lg text-xs font-medium"
                  :class="sample.is_active ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'"
                >
                  {{ sample.is_active ? $t('BUSINESS_DATA.GENERAL.ACTIVE') : $t('BUSINESS_DATA.GENERAL.INACTIVE') }}
                </span>
              </div>
             

              <!-- Description -->
              <p
                class="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-line break-words"
                v-html="hl(sample.description)"
              />

              <!-- Time + Actions Row -->
              <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
                <span class="text-xs text-slate-400">
                  {{ $t('BUSINESS_DATA.AI_SAMPLE_SENTENCES.UPDATED_AT', { time: formatTimestamp(sample.updated_at) }) }}
                </span>

                <!-- Action Buttons - Always visible on mobile, hover on desktop -->
                <div class="flex items-center gap-1 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                  <woot-button
                    variant="smooth"
                    size="small"
                    :icon="sample.is_active ? 'eye-hide' : 'eye-show'"
                    color-scheme="secondary"
                    v-tooltip.top="sample.is_active ? $t('GENERAL.TOOLTIPS.HIDE') : $t('GENERAL.TOOLTIPS.SHOW')"
                    @click="$emit('toggle-status', sample)"
                  />
                  <woot-button
                    variant="smooth"
                    color-scheme="secondary"
                    size="small"
                    icon="edit"
                    v-tooltip.top="$t('GENERAL.TOOLTIPS.EDIT')"
                    @click="$emit('edit-sample', sample)"
                  />
                  <woot-button
                    variant="smooth"
                    size="small"
                    icon="dismiss-circle"
                    color-scheme="alert"
                    v-tooltip.top="$t('GENERAL.TOOLTIPS.DELETE')"
                    @click="$emit('delete-sample', sample)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <TableFooter
      v-if="!isLoading && samples.length > 0 && pagination.total_pages > 1"
      :current-page="pagination.current_page"
      :total-count="pagination.total_count"
      :page-size="pagination.per_page"
      class="border-slate-50 dark:border-slate-800 border-t !px-0 py-4"
      @page-change="onPageChange"
    />
  </div>

  <ImportResultsModal
    :show="showImportResultsModal"
    :results="importResults || { created: 0, updated: 0, failed_count: 0, failed: [] }"
    @close="closeImportResults"
  />
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import { highlightText as highlightTextUtil } from 'shared/utils/searchHighlight';
import { triggerFileInput, getSelectedFileAndReset } from 'shared/utils/fileInput';
import { formatDateTimeLocalized } from 'shared/helpers/DateHelper';
import BulkActionsBar from './BulkActionsBar.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import ImportResultsModal from '../ImportResultsModal.vue';

export default {
  name: 'AiSampleSentencesTab',
  components: { 
    Spinner, 
    BulkActionsBar,
    TableFooter,
    ImportResultsModal,
  },
  props: {
    samples: { type: Array, default: () => [] },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
      }),
    },
    isLoading: { type: Boolean, default: false },
    isSearching: { type: Boolean, default: false },
    searchTerm: { type: String, default: '' },
    sort: { type: String, default: 'created_at_desc' },
    isExporting: { type: Boolean, default: false },
    isImporting: { type: Boolean, default: false },
    selectedIds: { type: Array, default: () => [] },
    isBulkDeleting: { type: Boolean, default: false },
    bulkAction: { type: String, default: '' },
    importResults: { type: Object, default: null },
  },
  emits: ['add-sample', 'edit-sample', 'delete-sample', 'search', 'sort-change', 'page-change', 'export', 'import', 'toggle-select', 'toggle-select-page', 'clear-selection', 'bulk-update-status', 'bulk-delete', 'toggle-status'],
  data() {
    return {
      searchTimer: null,
      searchQuery: '',
      selectedSort: 'created_at_desc',
      showImportResultsModal: false,
    }
  },
  computed: {
    hasBulkSelection() {
      return this.selectedIds.length > 0
    },
    selectedCount() {
      return this.selectedIds.length
    },
    isAllSelectedOnPage() {
      if (!this.samples.length) return false
      return this.samples.every(sample => this.selectedIds.includes(sample.id))
    },
  },
  watch: {
    searchTerm: {
      immediate: true,
      handler(value) {
        this.searchQuery = value || ''
      },
    },
    sort: {
      immediate: true,
      handler(value) {
        this.selectedSort = value || 'created_at_desc'
      },
    },
  },
  methods: {
    showImportResults() {
      this.showImportResultsModal = true
    },
    closeImportResults() {
      this.showImportResultsModal = false
    },
    // Shared helpers
    hl(text) {
      return highlightTextUtil(text, this.searchQuery, 'search-highlight')
    },
    emitBulkStatus(isActive) {
      this.$emit('bulk-update-status', isActive)
    },
    triggerImport() {
      triggerFileInput(this.$refs.fileInput)
    },
    onFileSelected(event) {
      const file = getSelectedFileAndReset(event)
      if (!file) return
      this.$emit('import', file)
    },
    onExport() {
      this.$emit('export')
    },
    triggerSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.$emit('search', this.searchQuery.trim())
      }, 300)
    },
    onSortChange() {
      this.$emit('sort-change', this.selectedSort)
    },
    onPageChange(page) {
      this.$emit('page-change', page)
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      return formatDateTimeLocalized(timestamp, {
        includeSeconds: false,
        localeOverride: this.$i18n?.locale,
      })
    },
    onToggleSelect(id, checked) {
      this.$emit('toggle-select', { id, checked })
    },
    toggleSelectOnPage() {
      const ids = this.samples.map(sample => sample.id)
      this.$emit('toggle-select-page', { ids, selectAll: !this.isAllSelectedOnPage })
    },
  },
}
</script>

<style scoped>
.search-highlight {
  background-color: #fef08a;
  color: #92400e;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
}

.dark .search-highlight {
  background-color: #fbbf24;
  color: #451a03;
}
</style>
